import { ClipboardList, Pencil, Plus, Trash2 } from "lucide-react";
import { Badge, Button, Card, EmptyState, LoadingState, Table, TD, TH, THead, TR } from "@/components/ui";
import type { DatasetSummary } from "./types";

export function DatasetList({
  datasets,
  loading,
  busy,
  onEdit,
  onDelete,
  onCreate,
}: {
  datasets: DatasetSummary[];
  loading: boolean;
  busy?: boolean;
  onEdit: (version: string) => void;
  onDelete: (version: string) => void;
  onCreate: () => void;
}) {
  if (loading) {
    return (
      <Card>
        <LoadingState label="Vragenlijsten laden…" />
      </Card>
    );
  }

  if (datasets.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={<ClipboardList className="h-5 w-5" />}
          title="Nog geen vragenlijsten"
          description="Maak een nieuwe vragenlijst aan of importeer er een als JSON."
          action={
            <Button variant="brand" onClick={onCreate}>
              <Plus className="h-4 w-4" />
              Nieuwe vragenlijst
            </Button>
          }
        />
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      <Table>
        <THead>
          <tr>
            <TH className="w-24">Versie</TH>
            <TH>Naam</TH>
            <TH className="w-24 text-right">Vragen</TH>
            <TH className="hidden md:table-cell">Domeinen</TH>
            <TH className="hidden w-32 lg:table-cell">Aangemaakt</TH>
            <TH className="w-44 text-right">
              <span className="sr-only">Acties</span>
            </TH>
          </tr>
        </THead>
        <tbody>
          {datasets.map((ds) => (
            <TR key={ds.version} className="transition-colors hover:bg-sl-grey-50">
              <TD>
                <Badge tone="dark" className="font-mono">
                  {ds.version}
                </Badge>
              </TD>
              <TD>
                <button onClick={() => onEdit(ds.version)} className="text-left font-medium hover:underline" disabled={busy}>
                  {ds.name}
                </button>
                {ds.description && <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{ds.description}</p>}
              </TD>
              <TD className="text-right tabular-nums">{ds.total_questions}</TD>
              <TD className="hidden md:table-cell">
                {ds.domains && ds.domains.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {ds.domains.map((d) => (
                      <Badge key={d}>{d}</Badge>
                    ))}
                  </div>
                ) : (
                  <span className="text-muted-foreground">–</span>
                )}
              </TD>
              <TD className="hidden whitespace-nowrap text-muted-foreground lg:table-cell">{ds.created_date || "–"}</TD>
              <TD className="text-right">
                <div className="flex justify-end gap-1">
                  <Button variant="outline" size="sm" onClick={() => onEdit(ds.version)} disabled={busy}>
                    <Pencil className="h-3.5 w-3.5" />
                    Bewerken
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDelete(ds.version)}
                    disabled={busy}
                    className="text-destructive hover:bg-[#FDECEC]"
                    title="Vragenlijst verwijderen"
                    aria-label={`Vragenlijst ${ds.version} verwijderen`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </TD>
            </TR>
          ))}
        </tbody>
      </Table>
    </Card>
  );
}
